import React, { useState } from 'react';

import { Grid, Menu, MenuItem, Typography } from '@mui/material';
import { useAuthGuard } from 'src/auth';
import { SelectButton, WhiteAlineButton } from 'src/components';
import { PreviewEyeIcon } from 'src/components/icons/PreviewEyeIcon';
import { VertDotsIcon } from 'src/components/icons/VertDotsIcon';
import { setEditorFullScreen } from 'src/editor/vars';
import { Apollo } from 'src/gql/generated';
import { doNothing } from 'src/helpers';
import { useNotificationDispatch } from 'src/hooks';
import { useEditorLoading } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

import { fullScreenNotificationPayload } from '../../menus/use-view-menu';

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
  },
  menuItem: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    justifyContent: 'flex-start',
  },
  overflowDots: {
    marginRight: theme.spacing(-1),
  },
}));

interface PublicActionButtonsProps {
  document: Pick<Apollo.Document, 'id' | '__typename' | 'variant' | 'isCommunityTemplate'> & {
    workflow?: Pick<Apollo.Workflow, 'id'> | null;
  };
}

export function PublicActionButtons({ document }: PublicActionButtonsProps): JSX.Element {
  const { classes } = useStyles();
  const isTemplate = document.variant === Apollo.DocumentVariant.Template;
  const { guardedCallback: handleUseOrEditDoc } = useAuthGuard(doNothing);
  const { guardedCallback: handleImportTemplate } = useAuthGuard(doNothing);

  return (
    <Grid component="div">
      <Grid container className={classes.container} sx={{ flexDirection: 'row' }}>
        <OverflowMenu />
        {document.isCommunityTemplate && (
          <WhiteAlineButton variant="outlined" onClick={handleImportTemplate}>
            Import Template
          </WhiteAlineButton>
        )}
        <WhiteAlineButton variant="filled" onClick={handleUseOrEditDoc}>
          {isTemplate ? 'Use Template' : 'Edit Document'}
        </WhiteAlineButton>
      </Grid>
    </Grid>
  );
}

const PreviewAction = React.forwardRef(function PreviewAction(
  {
    closeMenu,
    ...rest
  }: {
    closeMenu: () => void;
  },
  ref: React.Ref<HTMLLIElement>
): JSX.Element | null {
  const { classes } = useStyles();
  const dispatch = useNotificationDispatch();
  const editorLoading = useEditorLoading();

  function makeFullScreen() {
    closeMenu();
    setEditorFullScreen(true);
    setTimeout((): void => {
      dispatch(fullScreenNotificationPayload);
    }, 500);
  }

  return (
    <MenuItem
      {...rest}
      ref={ref}
      className={classes.menuItem}
      disabled={editorLoading}
      onClick={makeFullScreen}
    >
      <PreviewEyeIcon />
      <Typography>Preview</Typography>
    </MenuItem>
  );
});

// TODO: If changing, consider migrating to DeclarativeMenu
function OverflowMenu(): JSX.Element | null {
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <SelectButton
        ref={setAnchorEl}
        className={classes.overflowDots}
        selected={menuOpen}
        onClick={(): void => setMenuOpen(!menuOpen)}
      >
        <VertDotsIcon fill="white" />
      </SelectButton>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={menuOpen}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        variant="menu"
        onClose={closeMenu}
      >
        <PreviewAction closeMenu={closeMenu} />
      </Menu>
    </>
  );
}
